import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFilters } from "@/hooks/useFilters";
import PageHeader from "@/components/common/PageHeader";
import FilterBar from "@/components/common/FilterBar";
import { SkeletonCards, ErrorState } from "@/components/common/States";
import KpiGrid from "@/components/dashboard/KpiGrid";
import { PartnerPerformance, TeamPerformance, RecentActivity } from "@/components/dashboard/SidePanels";
import LeadTrendChart from "@/components/charts/LeadTrendChart";
import SalespersonChart from "@/components/charts/SalespersonChart";
import PipelineChart from "@/components/charts/PipelineChart";
import { Button } from "@/components/ui/button";
import { MapPin } from "lucide-react";

function Charts({ data }) {
  const k = data.kpis;
  const side = {
    super_admin: <PartnerPerformance rows={data.partner_performance} conversion={k.conversion_rate} workingNow={k.working_now} />,
    manager: <TeamPerformance rows={data.team_performance} />,
    field_executive: <RecentActivity rows={data.recent_activity} />,
  }[data.role];
  const comparison = ["super_admin", "manager"].includes(data.role);
  return (
    <>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <LeadTrendChart data={data.lead_trend} className={side ? "lg:col-span-2" : "lg:col-span-3"} />
        {side}
      </div>
      {data.pipeline && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {comparison && <SalespersonChart data={data.salesperson_comparison} />}
          <PipelineChart data={data.pipeline} className={comparison ? "" : "lg:col-span-2"} />
        </div>
      )}
    </>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { filters, setFilters, apiParams, query } = useFilters();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setError("");
    try {
      const { data } = await api.get("/dashboard", { params: apiParams });
      setData(data);
    } catch (e) {
      setError(apiError(e));
    }
  }, [apiParams]);
  useEffect(() => { load(); }, [load]);

  const firstName = (user?.full_name || "").split(" ")[0];

  return (
    <div className="space-y-6">
      <PageHeader
        title={`Good to see you, ${firstName}`}
        subtitle="Here's what's happening across your sales work."
        testid="dashboard-header"
        actions={user?.role === "field_executive" ? (
          <Button onClick={() => navigate("/work-map")} className="bg-teal hover:bg-teal-700 h-11" data-testid="dash-start-work-btn">
            <MapPin className="h-4 w-4" /> Start / End Work
          </Button>
        ) : null}
      />
      <FilterBar filters={filters} setFilters={setFilters} testid="dashboard-filter-bar" />
      {error ? <ErrorState message={error} onRetry={load} /> : !data ? <SkeletonCards /> : (
        <>
          <KpiGrid role={data.role} kpis={data.kpis} query={query} salesperson={filters.salesperson} />
          <Charts data={data} />
        </>
      )}
    </div>
  );
}
